import { Gauge, Maximize, Minimize, Pause, Play } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react'
import YouTube from 'react-youtube';
import { getYouTubeVideoId, getYouTubeVideoThumbnailById } from '../utils/common';

export default function VideoPlayer({ videoLink }) {
  const videoId = getYouTubeVideoId(videoLink);
  const [thumbnail, setThumbnail] = useState(getYouTubeVideoThumbnailById(videoId));
  const [player, setPlayer] = useState();
  const [vStatus, setVStatus] = useState(false);  //true=play, false=pause
  const [vTimeP, setVTimeP] = useState(0);  //true=play, false=pause
  const [vSpeed, setVSpeed] = useState(1.0);   //true=unMute, false=mute
  const [vFullScreen, setVFullScreen] = useState(false);  //true=fullScreen, false=notFullScreen
  const [vDuration, setVDuration] = useState("");
  const updateVideoTime = useRef();
  const jumpRange = 5;        //Used for forward or backward
  const [loading, setLoading] = useState(true);

  const opts = {
    playerVars: {
      autoplay: 0,
      mute: 0,
      loop: 1,
      color: "white",
      controls: 0,
      modestbranding: 1,
      playsinline: 1,
      rel: 0,
      enablejsapi: 1,
      showinfo: 0,
      ecver: 2
    }
  };

  const popUp = (content) => {
    const videoContainer = document.getElementById("videoContainer");
    const jumpIco = document.createElement("span");
    jumpIco.classList.add("jumpIco");
    jumpIco.innerText = content;
    videoContainer.appendChild(jumpIco);
    setTimeout(() => {
      videoContainer.removeChild(jumpIco);
    }, 1000);
  }

  const openSpinner = () => { document.getElementById("spinnerContainer").classList.remove("hidden"); }
  const closeSpinner = () => { document.getElementById("spinnerContainer").classList.add("hidden"); }

  const openFullscreen = (ele) => {
    if (ele.requestFullscreen) {
      ele.requestFullscreen();
    } else if (ele.webkitRequestFullscreen) {
      ele.webkitRequestFullscreen();
    } else if (ele.msRequestFullscreen) {
      ele.msRequestFullscreen();
    }
    setVFullScreen(() => true);
  }

  const closeFullscreen = () => {
    if (!!document.exitFullscreen) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } else if (!!document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (!!document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    setVFullScreen(() => false);
  }

  const toggleVideoFullScreen = () => {
    try {
      if (!vFullScreen) {
        openFullscreen(document.getElementById("fullScreenContainer"));
      } else {
        closeFullscreen();
      }
    } catch (error) {
      console.error(`Error on toggle video video: ${error}`);
    }
  }


  const onVideoPlay = () => {
    if (thumbnail) {
      setThumbnail(null);
    }
    setVStatus(true);
  }

  const onVideoReady = (event) => {
    setLoading(false);
    setPlayer((prevPlayer) => event.target);
    setVStatus(false);
    setVFullScreen(false);
    closeSpinner();

    const d = event.target.getDuration();
    setVDuration(`${Math.floor(d / 60)}:${((d % 60).toString().length < 2 ? "0" + (d % 60) : (d % 60))}`);
  }

  const onVideoEnd = () => {
    setVStatus(false);
    setVTimeP(100);
  }

  const playPause = () => {
    try {
      if (!!player) {
        vStatus ? player.pauseVideo() : player.playVideo();
        setVStatus(!vStatus);
        popUp(vStatus ? "Pause" : "Play");
      }
    } catch (error) {
      console.error(`Error on play pause video: ${error}`);
    }
  }

  const vForward = () => {
    try {
      if (!!player) {
        const time = player.getCurrentTime() + jumpRange;
        const percentage = Math.round((time / player.getDuration()) * 100);
        player.seekTo(time);
        setVTimeP(percentage);

        popUp("ᐅᐅ");
      }
    } catch (error) {
      console.error(`Error on forward video: ${error}`);
    }
  }

  const vBackward = () => {
    try {
      if (!!player) {
        const time = player.getCurrentTime() - jumpRange;
        const percentage = Math.round((time / player.getDuration()) * 100);
        player.seekTo(time);
        setVTimeP(percentage);

        popUp("ᐊᐊ");
      }
    } catch (error) {
      console.error(`Error on backward video: ${error}`);
    }
  }

  const handleSetSpeed = (value) => {
    try {
      player.setPlaybackRate(value);
      setVSpeed(value);
      popUp(`x${value}`)
    } catch (error) {
      console.error(`Error on set speed for video: ${error}`);
    }
  }

  const handleProgressChange = (event) => {
    try {
      const value = event.target.value;
      const time = Math.round(player.getDuration() * value / 100);
      setVTimeP(value);
      player.seekTo(time);
      popUp(`${value}%`);
    } catch (error) {
      console.error(`Error on change progress video: ${error}`);
    }
  }

  const jumpToPercentage = (percentage) => {
    try {
      if (!!player) {
        const time = (player.getDuration() * percentage) / 100;
        player.seekTo(time);
        setVTimeP(percentage);

        popUp(`${percentage}%`);
      }
    } catch (error) {
      console.error(`Error on jump to percentage video: ${error}`);
    }
  };

  useEffect(() => {
    if (vStatus) {
      updateVideoTime.current = setInterval(() => {
        const percentage = Math.round((player.getCurrentTime() / player.getDuration()) * 100)
        setVTimeP(percentage);
      }, 10);
    } else {
      clearInterval(updateVideoTime.current);
      updateVideoTime.current = null;
    }
  }, [player, vStatus]);


  const keyboardEventListener = (e) => {
    console.log(`Presses: ${e.code}`)
    try {
      if (!!player) {
        if (e.code === 'Space' || e.code === 'KeyK' || e.code === 'KeyS') {
          e.preventDefault();
          playPause();
        }
        if (e.code === 'KeyF' || e.code === 'KeyW') {
          e.preventDefault();
          toggleVideoFullScreen();
        }
        if (e.code === 'Escape') {
          e.preventDefault();
          closeFullscreen();
        }
        if (e.code === 'KeyJ' || e.code === 'KeyA' || e.code === 'ArrowLeft') {
          e.preventDefault();
          vBackward();
        }
        if (e.code === 'KeyL' || e.code === 'KeyD' || e.code === 'ArrowRight') {
          e.preventDefault();
          vForward();
        }
        if (e.key >= '0' && e.key <= '9') {
          const percentage = parseInt(e.key) * 10; // Convert key to percentage (e.g., '1' = 10%, '2' = 20%, etc.)
          jumpToPercentage(percentage);
        }
      }
    } catch (error) {
      console.error(`Error on key down: ${error}`);
    }

  }


  useEffect(() => {
    if (vStatus) {
      updateVideoTime.current = setInterval(() => {
        const percentage = Math.round((player.getCurrentTime() / player.getDuration()) * 100)
        setVTimeP(percentage);
      }, 10);
    } else {
      clearInterval(updateVideoTime.current);
      updateVideoTime.current = null;
    }
  }, [player, vStatus]);


  // Hide the mouse
  let timer = null;
  const handleMouseMove = () => {
    if (timer) { clearTimeout(timer) };
    const controllerContainer = document.getElementById("controllerContainer");
    controllerContainer.classList.add("opacity-100");
    timer = setTimeout(() => {
      controllerContainer.classList.remove("opacity-100");
    }, 1000);
  };

  useEffect(() => {
    window.addEventListener('keydown', keyboardEventListener);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener('keydown', keyboardEventListener);
      window.removeEventListener("mousemove", handleMouseMove);
      if (timer) clearTimeout(timer);
    };
  }, [keyboardEventListener, timer]);

  return (
    <>
      <div id="fullScreenContainer" className="relative" >
        {
          thumbnail && (
            <div className='w-full h-full bg-base-100 abs-center z-[1]' onClick={loading ? null : playPause}>

              {
                loading
                  ? <div className='loading loading-md loading-bars abs-center z-[2]' />
                  : <>
                    <img src={thumbnail} alt="Video Thumbnail" className="w-full h-full object-cover abs-center z-[1]" />
                    <Play fill='white' color='black' size={64} className='abs-center z-[2] shadow-md opacity-80 bg-transparent shadow-none' />
                  </>

              }
            </div>
          )
        }
        <div id='videoContainer' className={"overflow-hidden w-full aspect-video select-none relative " + (vFullScreen ? "fixed top-0 left-0 w-[100vw] h-[100vh] m-0 p-0 z-[120] border-none" : null)}>
          <div id='spinnerContainer' className="w-full h-full bg-base-100">
            <div id='spinner' className="loading loading-md abs-center" />
          </div>
          <YouTube
            videoId={videoId}
            title="Video Player"
            frameBorder="0"
            opts={opts}
            iframeClassName={'w-full h-full'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            onEnd={onVideoEnd}
            onReady={onVideoReady}
            onPlay={onVideoPlay}
            className="pointer-events-none w-[300%] h-full -ml-[100%]"
          />
          <div className="jump forward absolute top-0 h-full w-[20%] opacity-0 right-0 cursor-e-resize" onClick={vForward} />
          <div className="abs-center h-full w-[60%] opacity-0 cursor-pointer" onClick={playPause} />
          <div className="jump backward top-0 h-full w-[20%] opacity-0 left-0 cursor-w-resize" onClick={vBackward} />
        </div>
        <div id="controllerContainer" className={'flex ap-2 items-center bg-base-100 border-current overflow-visible ' + (vFullScreen ? "fixed bottom-0 left-0 w-[100vw] z-[121] opacity-0" : "")}>
          <button className="btn btn-ghost btn-square" onClick={playPause} id='playPauseBtn'>
            {vStatus ? <Pause /> : <Play />}
          </button>
          <div className='w-full h-full flex-center relative'>
            <div className="abs-center flex w-full justify-between items-center h-full px-2 text-xs z-0 pointer-events-none">
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
              <span className='h-[70%] w-1 bg-current opacity-80' />
            </div>
            <input classList="abs-center w-full h-full" type="range" value={vTimeP} step={1} min={0} max={100} className="range range-xs" onChange={handleProgressChange} />
          </div>
          <p className='ml-4'>{vDuration ?? "?:??"}</p>
          <div className="dropdown dropdown-top dropdown-hover">
            <div tabIndex={0} role="button" className="btn btn-ghost btn-square"><Gauge /></div>
            <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-[1] p-2 shadow">
              <li><button className={vSpeed === 0.25 ? "active" : " "} onClick={() => handleSetSpeed(0.25)}>0.25</button></li>
              <li><button className={vSpeed === 0.5 ? "active" : " "} onClick={() => handleSetSpeed(0.5)}>0.5</button></li>
              <li><button className={vSpeed === 1.0 ? "active" : " "} onClick={() => handleSetSpeed(1.0)}>1.0</button></li>
              <li><button className={vSpeed === 1.5 ? "active" : " "} onClick={() => handleSetSpeed(1.5)}>1.5</button></li>
              <li><button className={vSpeed === 2.0 ? "active" : " "} onClick={() => handleSetSpeed(2.0)}>2.0</button></li>
            </ul>
          </div>
          <button className="btn btn-ghost btn-square" id='fullScreenBtn' onClick={toggleVideoFullScreen}>
            {vFullScreen ? <Minimize /> : <Maximize />}
          </button>

        </div>
      </div>
    </>

  )
}
